import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Download, Copy, CheckCheck, RefreshCw, Play, ImagePlus, X } from "lucide-react";
import { useLanguage } from "../App";

const API = `/api`;

const ASPECT_RATIOS = [
  { value: "16:9", labelKey: "ar_landscape", w: 64, h: 36 },
  { value: "9:16", labelKey: "ar_portrait",  w: 36, h: 64 },
  { value: "1:1",  labelKey: "ar_square",    w: 50, h: 50 },
];

const DURATIONS = [
  { value: 4,  labelKey: "duration_4s" },
  { value: 8,  labelKey: "duration_8s" },
];

export default function VideoGenerator() {
  const { t } = useLanguage();

  const [prompt, setPrompt] = useState("");
  const [audioDetails, setAudioDetails] = useState("");
  const [visualStyle, setVisualStyle] = useState("");
  const [motionCamera, setMotionCamera] = useState("");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [duration, setDuration] = useState(4);
  const [quality, setQuality] = useState("standard");
  const [includeAudio, setIncludeAudio] = useState(true);

  const [isGenerating, setIsGenerating] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [copied, setCopied] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const fileInputRef = useRef(null);

  const pollingRef = useRef(null);
  const timerRef  = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRefImage(reader.result.split(",")[1]);
      setRefPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeRef = () => {
    setRefImage(null);
    setRefPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const suggestions = [t("vid_sug1"), t("vid_sug2"), t("vid_sug3"), t("vid_sug4")];

  useEffect(() => {
    return () => {
      clearInterval(pollingRef.current);
      clearInterval(timerRef.current);
    };
  }, []);

  const startPolling = (id) => {
    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/generate/video/status/${id}`);
        const { status, video_url, error: jobError, progress_stage, progress } = res.data;
        setVideoStatus(progress_stage || status);
        setProgress(progress || 0);
        if (status === "completed") {
          clearInterval(pollingRef.current);
          clearInterval(timerRef.current);
          setIsGenerating(false);
          setResult({ videoUrl: video_url, prompt: res.data.prompt });
        } else if (status === "failed") {
          clearInterval(pollingRef.current);
          clearInterval(timerRef.current);
          setIsGenerating(false);
          setError(jobError || t("error_generation"));
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 5000);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { setError(t("error_no_prompt")); return; }
    setIsGenerating(true);
    setError(null);
    setResult(null);
    setElapsed(0);
    setProgress(0);
    setVideoStatus("pending");
    timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
    try {
      const res = await axios.post(`${API}/generate/video`, {
        prompt: prompt.trim(),
        audio_details: audioDetails.trim() || undefined,
        visual_style: visualStyle.trim() || undefined,
        motion_camera: motionCamera.trim() || undefined,
        aspect_ratio: aspectRatio,
        duration,
        quality,
        include_audio: includeAudio,
        reference_image: refImage || undefined,
      });
      startPolling(res.data.job_id);
    } catch (err) {
      clearInterval(timerRef.current);
      setIsGenerating(false);
      setError(err.response?.data?.detail || t("error_generation"));
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const link = document.createElement("a");
    link.href = result.videoUrl;
    link.download = `khatira-video-${Date.now()}.mp4`;
    link.click();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setVideoStatus(null);
    setPrompt("");
  };

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const btnRowStyle = { flex: 1, padding: "0.55rem 0.5rem", borderRadius: "2px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", transition: "all 0.2s ease" };

  return (
    <div className="fade-in-up" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── STEP 1 ─────────────────────────────────────────── */}
      <div className="step-card">
        <StepLabel number="01" title={t("vid_step1_title")} desc={t("vid_step1_desc")} />

        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.22em", color: "rgba(201,168,76,0.5)", textTransform: "uppercase", marginBottom: "0.65rem" }}>
            {t("vid_suggestions_label")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {suggestions.map((s) => (
              <button key={s} className="suggestion-chip" data-testid="vid-suggestion-chip" onClick={() => setPrompt(s)}>{s}</button>
            ))}
          </div>
        </div>

        <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 400, color: "#B8956A", letterSpacing: "0.08em", display: "block", marginBottom: "0.5rem" }}>
          {t("vid_prompt_label")}
        </label>
        <textarea
          className="khatira-textarea"
          data-testid="video-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t("vid_prompt_placeholder")}
          style={{ minHeight: "130px" }}
        />
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "rgba(184,149,106,0.45)", marginTop: "0.5rem", fontWeight: 300 }}>
          {t("vid_prompt_note")}
        </p>
      </div>

      {/* ── STEP 2 ─────────────────────────────────────────── */}
      <div className="step-card">
        <StepLabel number="02" title={t("vid_step2_title")} desc={t("vid_step2_desc")} />
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {[
            { key: "vid_audio_label", val: audioDetails, set: setAudioDetails, ph: "vid_audio_placeholder", id: "vid-audio-input" },
            { key: "vid_style_label", val: visualStyle,  set: setVisualStyle,  ph: "vid_style_placeholder",  id: "vid-style-input" },
            { key: "vid_motion_label",val: motionCamera, set: setMotionCamera, ph: "vid_motion_placeholder", id: "vid-motion-input" },
          ].map((item) => (
            <div key={item.key} className="optional-section">
              <CollapsibleSection title={t(item.key)} optionalLabel={t("optional")}>
                <textarea
                  className="khatira-textarea"
                  data-testid={item.id}
                  value={item.val}
                  onChange={(e) => item.set(e.target.value)}
                  placeholder={t(item.ph)}
                  style={{ minHeight: "72px" }}
                />
              </CollapsibleSection>
            </div>
          ))}
        </div>
      </div>

      {/* Reference Image Upload */}
      <div className="optional-section">
        <CollapsibleSection title={t("ref_image_label")} optionalLabel={t("optional")}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: "rgba(184,149,106,0.6)", marginBottom: "0.75rem", fontWeight: 300 }}>
            {t("ref_image_desc")}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
            data-testid="vid-ref-upload-input"
          />
          {refPreview ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <img src={refPreview} alt="Reference" style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "2px", border: "1px solid rgba(201,168,76,0.2)" }} />
              <button data-testid="vid-ref-remove" onClick={removeRef} style={{ background: "none", border: "1px solid rgba(180,60,40,0.3)", borderRadius: "2px", color: "#D4836A", cursor: "pointer", padding: "0.35rem 0.7rem", fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <X size={12} /> {t("ref_image_remove")}
              </button>
            </div>
          ) : (
            <button
              data-testid="vid-ref-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              style={{ background: "rgba(201,168,76,0.06)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: "2px", padding: "1.5rem", cursor: "pointer", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#B8956A", fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem" }}
            >
              <ImagePlus size={24} style={{ color: "rgba(201,168,76,0.4)" }} />
              {t("ref_image_label")}
            </button>
          )}
        </CollapsibleSection>
      </div>

      {/* ── STEP 3 ─────────────────────────────────────────── */}
      <div className="step-card">
        <StepLabel number="03" title={t("vid_step3_title")} />
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.value}
              data-testid={`vid-ar-${ar.value.replace(":", "x")}`}
              className={`ar-btn ${aspectRatio === ar.value ? "active" : ""}`}
              onClick={() => setAspectRatio(ar.value)}
            >
              <div className="ar-icon" style={{ width: ar.w * 0.7, height: ar.h * 0.7 }} />
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: aspectRatio === ar.value ? "#D4B878" : "#B8956A", fontWeight: 400 }}>
                {ar.value}
              </span>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", color: "rgba(184,149,106,0.55)" }}>
                {t(ar.labelKey)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── STEP 4 ─────────────────────────────────────────── */}
      <div className="step-card">
        <StepLabel number="04" title={t("vid_step4_title")} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.5rem" }}>

          {/* Duration */}
          <div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: 400, color: "rgba(184,149,106,0.6)", marginBottom: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {t("vid_duration_label")}
            </p>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  data-testid={`vid-duration-${d.value}`}
                  onClick={() => setDuration(d.value)}
                  style={{
                    ...btnRowStyle,
                    border: `1px solid ${duration === d.value ? "#C9A84C" : "rgba(201,168,76,0.18)"}`,
                    background: duration === d.value ? "rgba(201,168,76,0.1)" : "transparent",
                    color: duration === d.value ? "#D4B878" : "#B8956A",
                    fontWeight: duration === d.value ? 500 : 300,
                  }}
                >
                  {d.value}s
                </button>
              ))}
            </div>
          </div>

          {/* Quality */}
          <div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: 400, color: "rgba(184,149,106,0.6)", marginBottom: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {t("vid_quality_label")}
            </p>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              {[{ val: "standard", lk: "vid_quality_standard" }, { val: "high", lk: "vid_quality_high" }].map((q) => (
                <button
                  key={q.val}
                  data-testid={`vid-quality-${q.val}`}
                  onClick={() => setQuality(q.val)}
                  style={{
                    ...btnRowStyle,
                    border: `1px solid ${quality === q.val ? "#C9A84C" : "rgba(201,168,76,0.18)"}`,
                    background: quality === q.val ? "rgba(201,168,76,0.1)" : "transparent",
                    color: quality === q.val ? "#D4B878" : "#B8956A",
                    fontWeight: quality === q.val ? 500 : 300,
                  }}
                >
                  {t(q.lk)}
                </button>
              ))}
            </div>
          </div>

          {/* Audio toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button
              data-testid="vid-audio-toggle"
              className={`khatira-switch ${includeAudio ? "on" : ""}`}
              onClick={() => setIncludeAudio((p) => !p)}
            />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#B8956A", fontWeight: 300 }}>
              {t("vid_audio_toggle")}
            </span>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div data-testid="vid-error" style={{ padding: "1rem 1.25rem", background: "rgba(180,60,40,0.08)", border: "1px solid rgba(180,60,40,0.25)", borderRadius: "2px", color: "#D4836A", fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", lineHeight: 1.6 }}>
          <strong style={{ display: "block", marginBottom: "0.3rem" }}>{t("error_generation")}</strong>
          <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>{error}</span>
        </div>
      )}

      {/* Generate Button */}
      {!isGenerating && !result && (
        <button
          data-testid="generate-video-btn"
          className="btn-primary"
          style={{ padding: "1rem", fontSize: "0.85rem", letterSpacing: "0.2em", width: "100%", textTransform: "uppercase" }}
          onClick={handleGenerate}
        >
          {t("vid_generate_btn")}
        </button>
      )}

      {/* Loading State */}
      {isGenerating && (
        <div data-testid="vid-loading" className="step-card" style={{ textAlign: "center", padding: "3.5rem 2rem" }}>
          <div
            className="pulse-glow"
            style={{
              width: 76,
              height: 76,
              margin: "0 auto 2rem",
              borderRadius: "50%",
              border: "1px solid rgba(201,168,76,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <div className="loading-ring spin-slow" style={{ position: "absolute", inset: -4, width: "calc(100% + 8px)", height: "calc(100% + 8px)", borderRadius: "50%" }} />
            <Play size={22} style={{ color: "#C9A84C" }} />
          </div>
          <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "1rem", fontWeight: 400, color: "#EDE0C4", marginBottom: "0.5rem", letterSpacing: "0.04em" }}>
            {t("vid_generating")}
          </h3>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: "#B8956A", marginBottom: "1rem", fontWeight: 300 }}>
            {t("vid_generating_sub")}
          </p>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: "rgba(184,149,106,0.45)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
            {t("vid_elapsed")}: {formatElapsed(elapsed)}
          </p>
          {videoStatus && (
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", color: "rgba(201,168,76,0.3)", marginTop: "0.4rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {videoStatus === "waiting_for_sora2" ? `sora 2 generating... ${progress > 0 ? `${progress}%` : ""}`
                : videoStatus === "saving_video" ? "saving video..."
                : videoStatus}
            </p>
          )}
        </div>
      )}

      {/* Output */}
      {result && (
        <div className="step-card fade-in-up" data-testid="vid-output-section">
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,168,76,0.55)", marginBottom: "1.25rem" }}>
            {t("out_video_ready")}
          </p>

          <div style={{ marginBottom: "1.5rem", borderRadius: "2px", overflow: "hidden", background: "#0A0502" }}>
            <video
              data-testid="generated-video"
              controls
              autoPlay
              style={{ width: "100%", display: "block", maxHeight: "480px" }}
              src={result.videoUrl}
            >
              Your browser does not support HTML5 video.
            </video>
          </div>

          <div style={{ background: "#120904", borderRadius: "2px", padding: "1rem 1.25rem", marginBottom: "1.25rem", border: "1px solid rgba(201,168,76,0.1)" }}>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,168,76,0.5)", marginBottom: "0.5rem" }}>
              {t("out_prompt_summary")}
            </p>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", color: "#B8956A", lineHeight: 1.65, fontWeight: 300 }}>
              {result.prompt}
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button data-testid="download-video-btn" className="btn-primary" style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }} onClick={handleDownload}>
              <Download size={13} /> {t("out_download")}
            </button>
            <button data-testid="copy-vid-prompt-btn" className="btn-ghost" style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }} onClick={handleCopy}>
              {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
              {copied ? t("out_copied") : t("out_copy")}
            </button>
            <button data-testid="vid-generate-another-btn" className="btn-ghost" style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }} onClick={handleGenerate}>
              <RefreshCw size={13} /> {t("out_another")}
            </button>
            <button data-testid="vid-new-project-btn" className="btn-ghost" style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem" }} onClick={handleReset}>
              {t("out_new")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */
function StepLabel({ number, title, desc }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginBottom: "1.25rem" }}>
      <span style={{ fontFamily: "'Cinzel', serif", fontSize: "1.8rem", fontWeight: 400, color: "rgba(201,168,76,0.25)", lineHeight: 1, minWidth: "2rem", flexShrink: 0 }}>
        {number}
      </span>
      <div>
        <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.95rem", fontWeight: 500, color: "#EDE0C4", margin: 0, letterSpacing: "0.04em" }}>
          {title}
        </h3>
        {desc && (
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "rgba(184,149,106,0.45)", margin: "0.2rem 0 0", fontWeight: 300 }}>
            {desc}
          </p>
        )}
      </div>
    </div>
  );
}

function CollapsibleSection({ title, optionalLabel, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="optional-trigger" onClick={() => setOpen(!open)} data-testid={`collapsible-vid-${title}`}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          {title}
          <span style={{ fontSize: "0.62rem", letterSpacing: "0.18em", color: "rgba(184,149,106,0.4)", textTransform: "uppercase", fontWeight: 300 }}>
            {optionalLabel}
          </span>
        </span>
        <span style={{ transition: "transform 0.25s ease", transform: open ? "rotate(45deg)" : "rotate(0deg)", fontSize: "1.1rem", lineHeight: 1, color: "rgba(201,168,76,0.5)" }}>
          +
        </span>
      </button>
      {open && <div className="optional-body">{children}</div>}
    </>
  );
}
